import React, { useState, useEffect } from 'react';
import { Popover, Spin, Comment, Avatar, Form, Input, Button } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import recipientIcon from './../.../../../../assets/svg/recipient.svg';
import clapIcon from './../.../../../../assets/svg/clap.svg';
import clapBlue from './../../../assets/svg/clap_blue.svg';
import { CoreBadge } from '../../../RnRCommonComponent';
import { LuCornerDownRight } from 'react-icons/lu';
import uuid from 'uuid/v4';
import { addCommas } from '../../../rewardsAndRecognitionHelper/utils.js';
import { downloadFromS3Signed } from 'src/_shared/services/utils';

const { TextArea } = Input;

dayjs.extend(relativeTime);

const getInitials = (user) => {
	const firstNameInitial = user?.firstName
		? user.firstName.charAt(0).toUpperCase()
		: '';
	const lastNameInitial = user?.lastName
		? user.lastName.charAt(0).toUpperCase()
		: '';
	return `${firstNameInitial}${lastNameInitial}`;
};

const Editor = ({ onChange, onSubmit, submitting, value, onCancel }) => (
	<>
		<Form.Item style={{ marginBottom: '12px' }}>
			<TextArea rows={3} onChange={onChange} value={value} placeholder="Write a reply..." />
		</Form.Item>
		<Form.Item style={{ marginBottom: '0' }}>
			<div style={{ display: 'flex', gap: '8px' }}>
				<Button htmlType="submit" loading={submitting} onClick={onSubmit} type="primary" size="small">
					Reply
				</Button>
				<Button onClick={onCancel} size="small">
					Cancel
				</Button>
			</div>
		</Form.Item>
	</>
);

const NewestFeed = (props) => {
	const {
		feedListData,
		handleClapCount,
		fetchMoreData,
		isLoadMoreCalled,
		hasMoreData,
		userAvatarSrc,
		handleReplySubmit: submitReply,
		currentUser
	} = props;

	const [replyStates, setReplyStates] = useState({});
	const [submittingReplies, setSubmittingReplies] = useState({});
	
	const [avatarUrls, setAvatarUrls] = useState({});

	useEffect(() => {
		const loadAvatars = async () => {
			const avatarKeys = new Set();
			
			feedListData?.forEach(item => {
				if (item?.awardedByUser?.avatar?.key) {
					avatarKeys.add(item.awardedByUser.avatar.key);
				}
				
				item?.comments?.forEach(comment => {
					if (comment.avatarKey) {
						avatarKeys.add(comment.avatarKey);
					}
				});
			});

			for (const avatarKey of avatarKeys) {
				try {
					const presignedUrl = await downloadFromS3Signed(avatarKey);
					setAvatarUrls(prev => ({ ...prev, [avatarKey]: presignedUrl }));
				} catch (error) {
					console.error('Error fetching avatar URL for key:', avatarKey, error);
				}
			}
		};

		if (feedListData?.length > 0) {
			loadAvatars();
		}
	}, [feedListData]);

	const toggleReplyEditor = (itemId) => {
		setReplyStates(prev => ({
			...prev,
			[itemId]: {
				...prev[itemId],
				showEditor: !prev[itemId]?.showEditor,
				value: prev[itemId]?.value || ''
			}
		}));
	};

	const handleReplyChange = (itemId, e) => {
		setReplyStates(prev => ({
			...prev,
			[itemId]: {
				...prev[itemId],
				value: e.target.value
			}
		}));
	};

	const handleReplySubmit = async (itemId) => {
		const replyText = replyStates[itemId]?.value?.trim();
		if (!replyText) return;

		setSubmittingReplies(prev => ({ ...prev, [itemId]: true }));

		try {
			const success = await submitReply(itemId, replyText);
			
			if (success) {
				setReplyStates(prev => ({
					...prev,
					[itemId]: {
						showEditor: false,
						value: ''
					}
				}));
			}
		} catch (error) {
			console.error('Error submitting reply:', error);
		} finally {
			setSubmittingReplies(prev => ({ ...prev, [itemId]: false }));
		}
	};

	const cancelReply = (itemId) => {
		setReplyStates(prev => ({
			...prev,
			[itemId]: {
				showEditor: false,
				value: ''
			}
		}));
	};

	const formatRecipients = (employees) => {
		if (!employees || employees.length === 0) return '';
		
		if (employees.length === 1) {
			return `${employees[0].firstName} ${employees[0].lastName}`;
		}
		
		if (employees.length <= 3) {
			return employees.map(emp => `${emp.firstName} ${emp.lastName}`).join(', ');
		}
		
		const firstThree = employees.slice(0, 3).map(emp => `${emp.firstName} ${emp.lastName}`).join(', ');
		return `${firstThree} and ${employees.length - 3} others`;
	};

	const reactionDetails = (reactionNames, reactionCount, hasClapped) => {
		if (reactionCount === 0 && !hasClapped) return null;
		const popoverContent = () => {
			return parseInt(reactionCount) ? (
				<div style={{
					maxHeight: '300px',
					overflowY: 'scroll',
					textOverflow: 'ellipsis',
					width: '130px',
				}}>
					{reactionNames?.map(name => (
						<div className="feed-author">
							<div className="feed-img" style={{display: 'inline-block', margin: '0 4px 4px 0'}}>
								<img src={recipientIcon} alt="img" />
							</div>
							<span key={uuid()}>{name}</span>
						</div>
					))}
				</div>
			) : null
		};

		return (
			<div style={{fontSize: '12px', marginRight: '6px'}}>
				<Popover
					overlayInnerStyle={{width: '100%'}}
					trigger="hover"
					content={reactionCount ? popoverContent : null}
				>
					<span style={{cursor: 'default'}}>
						{(hasClapped ? 'You' : reactionNames[0]) +
							(reactionCount ? ` and ${reactionCount} others` : '') +
							(hasClapped ? ' clapped' : '')
						}
					</span>
				</Popover>
			</div>
	)};

	// Use the GraphQL feed data directly
	const feedData = feedListData || [];

	return (
		<>
			{feedData &&
				feedData?.map((item, index) => {
					const points = item?.points ?? 0;
					const messageText = item?.content?.message || '';
					const messageImage = item?.content?.gifURL || null;
					const hasClapped = item?.hasClapped || false;
					const addOrRemoveClap = () => handleClapCount(item.id);
					const formatedCreatedAt = dayjs(item?.createdAt).fromNow();
					const reactionNames = item?.hasClappedUsers?.filter(user => user.id !== currentUser?.id).map(user => `${user.firstName} ${user.lastName}`) || [];
					const reactionCount = (item?.totalClaps ?? 0) - (hasClapped ? 1 : 0);
					const renderReactions = reactionDetails(reactionNames, reactionCount, hasClapped);
					const badge = item?.badge || {};
					const { label, color, gradientColor } = badge;
					const badgeTypeCode = badge?.badgeType?.code;
					const iconCode = badge?.icon?.code;

					// Handle multiple recipients or single employee
					const recipients = item?.additionalRecipients || (item?.employee ? [item.employee] : []);
					const recipientText = formatRecipients(recipients);
					const sentByName = (
						<div style={{display: 'flex', flexDirection: 'row'}}>
						<div>
							<span>{`${item?.awardedByUser?.firstName} ${item?.awardedByUser?.lastName}`}</span>
							<span style={{marginLeft: '8px',color: '#ccc', fontSize: '12px'}}>{formatedCreatedAt}</span>
							<div style={{marginLeft: '4px', display: 'flex', alignItems: 'baseline'}}>
								<LuCornerDownRight />
								<span style={{marginLeft: 4}}>
									@{recipientText}
								</span>
								{points > 0 && (
									<span style={{
										color: '#52c41a',
										fontWeight: 'bold',
										fontSize: '12px',
										whiteSpace: 'nowrap',
										marginLeft: '6px'
									}}>
										+{addCommas(points)} points
									</span>
								)}
							</div>
						</div>
						<div style={{display: 'inline-flex', marginLeft: '4px'}}>
						{badgeTypeCode && (
									<div style={{
										width: '36px',
										height: '36px',
										overflow: 'visible',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										flexShrink: 0,
										cursor: 'pointer'
									}}
									onMouseEnter={(e) => {
										const badgeElement = e.currentTarget.querySelector('.badge-transform');
										if (badgeElement) {
											badgeElement.style.transform = 'scale(0.9)';
										}
									}}
									onMouseLeave={(e) => {
										const badgeElement = e.currentTarget.querySelector('.badge-transform');
										if (badgeElement) {
											badgeElement.style.transform = 'scale(0.2)';
										}
									}}>
										<div
											className="badge-transform"
											style={{
												transform: 'scale(0.2)',
												transformOrigin: 'center',
												transition: 'transform 0.3s ease'
											}}>
											<CoreBadge
												useTilt={false}
												badgeType={badgeTypeCode}
												badgeIcon={iconCode}
												label={label}
												color={color}
												gradientColor={gradientColor}
											/>
										</div>
									</div>
								)}
								</div>
						</div>
					);

					// Create content with message and optional image
					const content = (
						<div>
							{messageText && (
								<div style={{ wordWrap: 'break-word', marginBottom: messageImage ? '12px' : '0' }}>
									{messageText}
								</div>
							)}
							{messageImage && (
								<div style={{ display: 'flex', justifyContent: 'center', marginTop: '8px' }}>
									<img
										src={messageImage}
										style={{ maxWidth: '100%', maxHeight: '236px' }}
										alt="Feed content"
									/>
								</div>
							)}
						</div>
					);

					// Create actions array
					const actions = [
						<div key="clap" style={{ display: 'flex', alignItems: 'baseline', height: '21px'}}>
							<span
								onClick={addOrRemoveClap}
								style={{ cursor: 'pointer', marginRight: 4 }}
							>
								<img
									src={hasClapped ? clapBlue : clapIcon}
									style={{ verticalAlign: 'bottom' }}
									className="clap-icon"
									alt="clap"
								/>
							</span>
							{renderReactions || '' }
						</div>,
						<span
							key="reply"
							onClick={() => toggleReplyEditor(item.id)}
							style={{ cursor: 'pointer', fontWeight: 900 }}
						>
							Reply
						</span>
					];

					// Get replies for this item from the comments (now at record level)
					const replies = (item?.comments || []).map(comment => ({
						id: comment.userId + '_' + comment.createdAt, // Create unique ID
						sentBy: {
							id: comment.userId,
							firstName: comment.firstName,
							lastName: comment.lastName,
							avatar: comment.avatarKey ? { key: comment.avatarKey } : null
						},
						content: {
							message: comment.message,
							gifURL: null
						},
						createdAt: comment.createdAt
					}));

					return (
						<React.Fragment key={index}>
							<div className="feed-card" style={{ marginBottom: '16px', position: 'relative' }}>
								<Comment
									author={sentByName}
									avatar={
										item?.awardedByUser?.avatar?.key && avatarUrls[item.awardedByUser.avatar.key] ? (
											<Avatar src={avatarUrls[item.awardedByUser.avatar.key]} />
										) : (
											<Avatar>{getInitials(item?.awardedByUser)}</Avatar>
										)
									}
									content={content}
									actions={actions}
								>
									{/* Render replies */}
									{replies.map(reply => {
										const replyAuthor = `${reply.sentBy.firstName} ${reply.sentBy.lastName}`;
										const replyContent = reply.content.message;
										const replyDate = dayjs(reply.createdAt).fromNow();

										return (
											<Comment
												key={reply.id}
												author={replyAuthor}
												avatar={
													reply?.sentBy?.avatar?.key && avatarUrls[reply.sentBy.avatar.key] ? (
														<Avatar src={avatarUrls[reply.sentBy.avatar.key]} />
													) : (
														<Avatar>{getInitials(reply?.sentBy)}</Avatar>
													)
												}
												content={<div style={{ wordWrap: 'break-word' }}>{replyContent}</div>}
												datetime={replyDate}
											/>
										);
									})}
									
									{/* Reply Editor */}
									{replyStates[item.id]?.showEditor && (
										<Comment
											avatar={
												userAvatarSrc ? (
													<Avatar src={userAvatarSrc} />
												) : (
													<Avatar>{getInitials(currentUser)}</Avatar>
												)
											}
											content={
												<Editor
													onChange={(e) => handleReplyChange(item.id, e)}
													onSubmit={() => handleReplySubmit(item.id)}
													submitting={submittingReplies[item.id] || false}
													value={replyStates[item.id]?.value || ''}
													onCancel={() => cancelReply(item.id)}
												/>
											}
										/>
									)}
								</Comment>
							</div>
							{index < feedData.length - 1 && (
								<div className="comment-spacer" style={{
									height: '20px',
									borderBottom: '1px solid #e0e0e0',
									margin: '16px 0',
									width: '100%'
								}} />
							)}
						</React.Fragment>
					);
				})}
			{hasMoreData ? (
				<div>
					{isLoadMoreCalled ? (
						<Spin className="d-flex align-items-center justify-content-center w-100 h-100" />
					) : (
						<div
							className="cursor-pointer d-flex align-items-center justify-content-center"
							onClick={() => {
								fetchMoreData();
							}}
						>
							<ReloadOutlined className="mr-1" />
							Load More
						</div>
					)}
				</div>
			) : (
				<></>
			)}
		</>
	);
};

export default NewestFeed;
